import type { TeamBrandingPublic } from "@connected-repo/zod-schemas/team_app.zod";

/**
 * Stock ConferHall identity — used everywhere a tenant leaves a field unset.
 * The files are copies of `brand/` at the repo root (see brand/README.md);
 * `themeColor` is the brand indigo the mark is drawn in.
 */
export const DEFAULT_BRAND = {
	appName: "ConferHall",
	shortName: "ConferHall",
	tagline: "Events, stages and live translation for your conferences",
	themeColor: "#4f46e5",
	backgroundColor: "#ffffff",
	iconUrl: "/apple-touch-icon.png",
	logoUrl: null as string | null,
} as const;

/**
 * ConferHall's own logo files, shown only on the shared app — a tenant host
 * shows the team's `logoUrl` instead. `white` variants are for dark surfaces.
 */
export const CONFERHALL_LOGO = {
	mark: "/brand/conferhall-mark.svg",
	markWhite: "/brand/conferhall-mark-white.svg",
	lockup: "/brand/conferhall-lockup.svg",
	lockupWhite: "/brand/conferhall-lockup-white.svg",
} as const;

export interface ResolvedBrand {
	appName: string;
	shortName: string;
	tagline: string;
	themeColor: string;
	backgroundColor: string;
	iconUrl: string;
	logoUrl: string | null;
	primaryColor: string | null;
	secondaryColor: string | null;
}

/** Merge a tenant's branding over the stock defaults. */
export const resolveBrand = (branding: TeamBrandingPublic | null | undefined): ResolvedBrand => {
	if (!branding) {
		return {
			...DEFAULT_BRAND,
			primaryColor: null,
			secondaryColor: null,
		};
	}
	const appName = branding.appName ?? branding.name ?? DEFAULT_BRAND.appName;
	return {
		appName,
		shortName: branding.appShortName ?? appName.slice(0, 12),
		tagline: branding.tagline ?? DEFAULT_BRAND.tagline,
		themeColor: branding.primaryColor ?? DEFAULT_BRAND.themeColor,
		backgroundColor: branding.backgroundColor ?? DEFAULT_BRAND.backgroundColor,
		iconUrl: branding.pwaIconUrl ?? branding.logoUrl ?? DEFAULT_BRAND.iconUrl,
		logoUrl: branding.logoUrl ?? branding.pwaIconUrl ?? null,
		primaryColor: branding.primaryColor,
		secondaryColor: branding.secondaryColor,
	};
};
